import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { IoHomeSharp, IoLinkSharp } from "react-icons/io5";
import { IoIosApps } from "react-icons/io";

import { NavItem } from "@/@types/nav-item";
import VerticalNavbar from "@/components/navbar/vertical-navbar";
import HorizontalNavbar from "@/components/navbar/horizontal-navbar";

import { useUser } from "@/contexts/UserContext";
import Home from "@/pages/admin-dashboard/projects";
import ProductsCatalog from "@/pages/admin-dashboard/products-catalog";
import InternalLinks from "@/pages/admin-dashboard/staff-restricted-views/links";
import { useAuthContext } from "@/contexts/AuthContext";

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { userData } = useUser();
  const { refresh } = useAuthContext();

  // Define navigation items
  const navItems: NavItem[] = [
    {
      title: t("dashboard.content.home.title"),
      icon: <IoHomeSharp />,
      content: <Home />,
      public: true,
    },
    {
      title: t("dashboard.content.products.title"),
      icon: <IoIosApps />,
      content: <ProductsCatalog />,
      public: true,
    },
    {
      title: t("dashboard.content.internal.links.title"),
      icon: <IoLinkSharp />,
      content: <InternalLinks />,
      public: false,
    },
  ];

  // Filter navigation items based on user role
  const filteredNavItems = navItems.filter(
    (item) => item.public || userData?.is_internal,
  );

  const [selectedItem, setSelectedItem] = useState<NavItem>(
    filteredNavItems[0],
  );

  // Memoized handler to select an item
  const handleSelectItem = useCallback((item: NavItem) => {
    setSelectedItem(item);
  }, []);

  // Reset session validity on tab visibility change
  useEffect(() => {    
    const handleVisibilityChange = async () => {
      if (document.hidden) return;
      await refresh();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <main className="flex flex-row w-full h-screen ">
      <div className={"flex w-full"}>
        <VerticalNavbar
          navItems={filteredNavItems}
          handleSelectedItem={handleSelectItem}
          size={12}
          className={`bg-gray-100 border-r-[2px] border-gray-200 w-12`}
        />
        <div className="flex flex-col w-full h-full">
          <HorizontalNavbar/>
          <section className={"flex flex-col  pl-20 pr-6 bg-gray-200 py-6"}>
            {selectedItem.content}
          </section>
        </div>
      </div>
    </main>
  );
};

export default AdminDashboard;
